import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatDate, type CourseEdition, type Enrollment } from "@/lib/api";

export default function InstructorCursoPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [edition, setEdition] = useState<CourseEdition | null>(null);
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [attendanceMap, setAttendanceMap] = useState<Record<string, string>>({});
  const [obsMap, setObsMap] = useState<Record<string, string>>({});
  const [editingObs, setEditingObs] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!id) return;
    (async () => {
      setLoading(true);
      try {
        const editionData = await api.getEdition(id);
        setEdition(editionData);
        const enrs = editionData.enrollments ?? [];
        setEnrollments(enrs);
        const attInit: Record<string, string> = {};
        const obsInit: Record<string, string> = {};
        enrs.forEach((e) => {
          attInit[e.id] = e.attendance_status;
          obsInit[e.id] = e.observations;
        });
        setAttendanceMap(attInit);
        setObsMap(obsInit);
      } catch {
        setEdition(null);
        setEnrollments([]);
      }
      setLoading(false);
    })();
  }, [id]);

  const handleAttendanceChange = (enrId: string, status: string) => {
    setAttendanceMap((prev) => ({ ...prev, [enrId]: status }));
    setSaved(false);
  };

  const handleObsChange = (enrId: string, value: string) => {
    setObsMap((prev) => ({ ...prev, [enrId]: value }));
  };

  const saveAttendance = async () => {
    setSaving(true);
    setError("");
    setSaved(false);
    try {
      const toUpdate = enrollments.filter((enr) => {
        const att = attendanceMap[enr.id] ?? enr.attendance_status;
        const obs = obsMap[enr.id] ?? enr.observations;
        return att !== enr.attendance_status || obs !== enr.observations;
      });

      await Promise.all(
        toUpdate.map((enr) =>
          api.updateAttendance(enr.id, {
            attendance_status: attendanceMap[enr.id] ?? enr.attendance_status,
            observations: obsMap[enr.id] ?? enr.observations,
          })
        )
      );
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al guardar asistencia");
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="text-center py-20">
        <i className="ri-loader-4-line text-3xl text-[var(--color-text-muted)] animate-spin"></i>
        <p className="text-sm text-[var(--color-text-muted)] mt-3">Cargando edición...</p>
      </div>
    );
  }

  if (!edition) {
    return (
      <div className="text-center py-20">
        <p className="text-[var(--color-text-muted)]">Edición no encontrada</p>
        <button onClick={() => navigate("/portal-instructor")} className="btn-primary mt-4 cursor-pointer">
          Volver
        </button>
      </div>
    );
  }

  const courseName = edition.course?.name || "Curso";
  const presentCount = enrollments.filter(
    (e) => (attendanceMap[e.id] ?? e.attendance_status) === "presente"
  ).length;
  const absentCount = enrollments.filter(
    (e) => (attendanceMap[e.id] ?? e.attendance_status) === "ausente"
  ).length;
  const registeredCount = enrollments.filter(
    (e) => (attendanceMap[e.id] ?? e.attendance_status) !== "pendiente"
  ).length;

  return (
    <div className="space-y-6 animate-[fadeIn_0.3s_ease-out]">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate("/portal-instructor")}
          className="text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-line text-xl"></i>
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">{courseName}</h1>
          <p className="text-sm text-[var(--color-text-muted)] mt-1">
            {edition.edition_label ? `${edition.edition_label} · ` : ""}
            {formatDate(edition.start_date)} &mdash; {formatDate(edition.end_date)} | {edition.location}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <div className="card py-2 px-4 text-center">
          <span className="text-sm font-bold text-[var(--color-text-primary)]">{enrollments.length}</span>
          <span className="text-xs text-[var(--color-text-muted)] ml-1">Total</span>
        </div>
        <div className="card py-2 px-4 text-center">
          <span className="text-sm font-bold text-green-400">{presentCount}</span>
          <span className="text-xs text-[var(--color-text-muted)] ml-1">Presentes</span>
        </div>
        <div className="card py-2 px-4 text-center print:hidden">
          <span className="text-sm font-bold text-red-400">{absentCount}</span>
          <span className="text-xs text-[var(--color-text-muted)] ml-1">Ausentes</span>
        </div>
        <div className="ml-auto flex items-center gap-3 print:hidden">
          <button
            onClick={() => window.print()}
            className="btn-secondary flex items-center gap-2 cursor-pointer"
          >
            <i className="ri-printer-line"></i> Imprimir Asistencia
          </button>
          <button
            onClick={saveAttendance}
            disabled={saving}
            className="btn-primary flex items-center gap-2 cursor-pointer"
          >
            <i className="ri-save-line"></i>
            {saving ? "Guardando..." : "Guardar Asistencia"}
          </button>
        </div>
      </div>

      {saved && (
        <div className="bg-green-500/10 border border-green-500/20 rounded-lg px-4 py-3 flex items-start gap-3">
          <i className="ri-checkbox-circle-line text-green-400 mt-0.5"></i>
          <p className="text-sm text-green-400">
            Asistencia registrada correctamente. {registeredCount} de {enrollments.length} estudiantes procesados.
          </p>
        </div>
      )}

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3 text-sm text-red-400">
          {error}
        </div>
      )}

      <div className="card overflow-hidden p-0 print:shadow-none print:border-none print:m-0">
        <div className="overflow-x-auto print:overflow-visible">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[var(--color-border)] print:border-black">
                <th className="table-header print:text-black">Estudiante</th>
                <th className="table-header print:text-black">Empresa</th>
                <th className="table-header print:text-black print:hidden">Contacto Emergencia</th>
                <th className="table-header text-center print:text-black print:hidden">Presente</th>
                <th className="table-header text-center print:text-black print:hidden">Ausente</th>
                <th className="table-header hidden print:table-cell print:text-black text-center">Firma Asistencia</th>
                <th className="table-header print:text-black">Observaciones</th>
              </tr>
            </thead>
            <tbody>
              {enrollments.map((enr) => {
                const attStatus = attendanceMap[enr.id] ?? enr.attendance_status;
                const obs = obsMap[enr.id] !== undefined ? obsMap[enr.id] : enr.observations;

                return (
                  <tr key={enr.id} className="hover:bg-[var(--color-bg-card-hover)] transition-colors">
                    <td className="table-cell">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-lg bg-[var(--color-accent)]/15 flex items-center justify-center flex-shrink-0">
                          <span className="text-[var(--color-text-accent)] text-[10px] font-semibold">
                            {(enr.student?.full_name || "?").charAt(0)}
                          </span>
                        </div>
                        <span className="text-sm font-medium text-[var(--color-text-primary)]">
                          {enr.student?.full_name || "Desconocido"}
                        </span>
                      </div>
                    </td>
                    <td className="table-cell">
                      <span className="text-xs text-[var(--color-text-secondary)]">
                        {enr.student?.company || "—"}
                      </span>
                    </td>
                    <td className="table-cell print:hidden">
                      <div>
                        <span className="text-sm text-[var(--color-text-secondary)]">{enr.emergency_contact}</span>
                        <p className="text-xs text-[var(--color-text-muted)]">{enr.emergency_phone}</p>
                      </div>
                    </td>
                    <td className="table-cell text-center print:hidden">
                      <button
                        onClick={() => handleAttendanceChange(enr.id, "presente")}
                        className={`w-9 h-9 rounded-lg flex items-center justify-center transition-all cursor-pointer ${
                          attStatus === "presente"
                            ? "bg-green-500/20 text-green-400 border border-green-500/30"
                            : "bg-[var(--color-bg-tertiary)] text-[var(--color-text-muted)] hover:bg-green-500/10 hover:text-green-400"
                        }`}
                      >
                        <i className="ri-check-line"></i>
                      </button>
                    </td>
                    <td className="table-cell text-center print:hidden">
                      <button
                        onClick={() => handleAttendanceChange(enr.id, "ausente")}
                        className={`w-9 h-9 rounded-lg flex items-center justify-center transition-all cursor-pointer ${
                          attStatus === "ausente"
                            ? "bg-red-500/20 text-red-400 border border-red-500/30"
                            : "bg-[var(--color-bg-tertiary)] text-[var(--color-text-muted)] hover:bg-red-500/10 hover:text-red-400"
                        }`}
                      >
                        <i className="ri-close-line"></i>
                      </button>
                    </td>
                    <td className="table-cell hidden print:table-cell border-b border-black/20"></td>
                    <td className="table-cell">
                      {editingObs === enr.id ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={obs}
                            onChange={(e) => handleObsChange(enr.id, e.target.value)}
                            className="input-field py-1.5 text-xs"
                            placeholder="Añadir observación..."
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === "Enter") setEditingObs(null);
                              if (e.key === "Escape") setEditingObs(null);
                            }}
                          />
                          <button
                            onClick={() => setEditingObs(null)}
                            className="text-green-400 hover:text-green-300 cursor-pointer"
                          >
                            <i className="ri-check-line"></i>
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-[var(--color-text-secondary)] max-w-[200px] truncate">
                            {obs || (
                              <span className="text-[var(--color-text-muted)] italic text-xs">
                                Sin observaciones
                              </span>
                            )}
                          </span>
                          <button
                            onClick={() => setEditingObs(enr.id)}
                            className="text-[var(--color-text-muted)] hover:text-[var(--color-text-accent)] cursor-pointer flex-shrink-0"
                          >
                            <i className="ri-edit-line text-xs"></i>
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
